import { useEffect, useMemo, useState } from 'react';

import { renderInfinityChannels } from '@components/Product/Pricelists/functions';
import { renderHeaderWithHover } from '@components/Table/Columns';
import {
  BootstrapTable,
  BootstrapVerticalColumnTable,
} from '@components/Table/Table';
import {
  CAPACITY_RATE_TYPES,
  METERED_SERVICES,
  PAYMENT_MODEL,
} from '@config/const';
import { planDetailsApi } from '@reducers/plan';

export const MeteredPlanPricingNonUsageTable = ({ data }) => {
  const query = planDetailsApi.useGetFieldDescriptionQuery();
  const fieldDescriptions = query.data || {};
  const columns = useMemo(
    () => [
      {
        Header: () => renderHeaderWithHover('NRC', 'nrc', fieldDescriptions),
        accessor: 'did_nrc',
        className: 'col-md-1',
      },
      {
        Header: () => renderHeaderWithHover('MRC', 'mrc', fieldDescriptions),
        accessor: 'did_mrc',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Default channels',
            'default_channels',
            fieldDescriptions
          ),
        accessor: 'default_channels',
        className: 'col-md-2',
        Cell: renderInfinityChannels,
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Max channels',
            'max_channels',
            fieldDescriptions
          ),
        accessor: 'max_channels',
        className: 'col-md-2',
        Cell: renderInfinityChannels,
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'NRC extra channel',
            'channel_nrc',
            fieldDescriptions
          ),
        accessor: 'channel_nrc',
        className: 'col-md-2',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'MRC extra channel',
            'channel_mrc',
            fieldDescriptions
          ),
        accessor: 'channel_mrc',
        className: 'col-md-2',
      },
    ],
    []
  );
  return data.length > 0 ? (
    <BootstrapTable
      columns={columns}
      data={data}
      tableProps={() => ({
        responsive: true,
        bordered: true,
        className: 'normalFontSize',
      })}
      tableHeader="Non-usage"
    />
  ) : null;
};

export const CapacityPlanPricingNonUsageTable = ({ data }) => {
  const rateType = data.length > 0 ? data[0].rate_type : PAYMENT_MODEL.CAPACITY;
  const query = planDetailsApi.useGetFieldDescriptionQuery();
  const fieldDescriptions = query.data || {};
  const meteredColumns = useMemo(
    () => [
      {
        Header: 'DID',
        className: 'text-center',
        columns: [
          {
            Header: () =>
              renderHeaderWithHover('NRC', 'nrc', fieldDescriptions),
            accessor: 'did_nrc',
            className: 'col-md-1',
          },
          {
            Header: () =>
              renderHeaderWithHover('MRC', 'mrc', fieldDescriptions),
            accessor: 'did_mrc',
            className: 'col-md-1',
          },
        ],
      },
      {
        Header: 'Dedicated channels',
        className: 'text-center',
        columns: [
          {
            Header: () =>
              renderHeaderWithHover(
                'Default channels',
                'default_channels',
                fieldDescriptions
              ),
            accessor: 'default_channels',
            className: 'col-md-1',
            Cell: renderInfinityChannels,
          },
          {
            Header: () =>
              renderHeaderWithHover(
                'Max channels',
                'max_channels',
                fieldDescriptions
              ),
            accessor: 'max_channels',
            className: 'col-md-1',
            Cell: renderInfinityChannels,
          },
          {
            Header: () =>
              renderHeaderWithHover(
                'NRC extra channel',
                'channel_nrc',
                fieldDescriptions
              ),
            accessor: 'channel_nrc',
            className: 'col-md-1',
          },
          {
            Header: () =>
              renderHeaderWithHover(
                'MRC extra channel',
                'channel_mrc',
                fieldDescriptions
              ),
            accessor: 'channel_mrc',
            className: 'col-md-1',
          },
        ],
      },
    ],
    []
  );
  const capacityColumns = useMemo(
    () => [
      {
        Header: 'DID with 2 channels',
        className: 'text-center',
        columns: [
          {
            Header: () =>
              renderHeaderWithHover('NRC', 'channel_nrc', fieldDescriptions),
            accessor: 'did_nrc',
            className: 'col-md-1',
          },
          {
            Header: () =>
              renderHeaderWithHover('MRC', 'channel_mrc', fieldDescriptions),
            accessor: 'did_mrc',
            className: 'col-md-1',
          },
        ],
      },
      {
        Header: 'Shared channels',
        className: 'text-center',
        columns: [
          {
            Header: renderHeaderWithHover(
              'NRC',
              'shared_channel_nrc',
              fieldDescriptions
            ),
            accessor: 'shared_channel_nrc',
            className: 'col-md-1',
          },
          {
            Header: renderHeaderWithHover(
              'MRC 1-10',
              'shared_channel_mrc_lt_10',
              fieldDescriptions
            ),
            accessor: 'shared_channel_mrc_lt_10',
            className: 'col-md-1',
          },
          {
            Header: renderHeaderWithHover(
              'MRC 11-100',
              'shared_channel_mrc_10_to_100',
              fieldDescriptions
            ),
            accessor: 'shared_channel_mrc_10_to_100',
            className: 'col-md-1',
          },
          {
            Header: renderHeaderWithHover(
              'MRC > 100',
              'shared_channel_mrc_gt_100',
              fieldDescriptions
            ),
            accessor: 'shared_channel_mrc_gt_100',
            className: 'col-md-1',
          },
          {
            Header: () =>
              renderHeaderWithHover(
                'Max',
                'max_shared_channels',
                fieldDescriptions
              ),
            accessor: 'max_shared_channels',
            className: 'col-md-1',
          },
        ],
      },
    ],
    []
  );
  const columns = {
    [CAPACITY_RATE_TYPES.CAPACITY]: capacityColumns,
    [CAPACITY_RATE_TYPES.METERED]: meteredColumns,
    [CAPACITY_RATE_TYPES.HYBRID]: meteredColumns,
  };
  const tableHeader = () => {
    const Header = () =>
      renderHeaderWithHover('Rate Type', 'rate_type', fieldDescriptions);
    return (
      <>
        <Header />: {rateType}
      </>
    );
  };
  return data.length > 0 ? (
    <BootstrapTable
      columns={columns[rateType]}
      data={data}
      tableProps={() => ({
        responsive: true,
        bordered: true,
        className: 'normalFontSize',
      })}
      tableHeader={tableHeader()}
    />
  ) : null;
};

export class PaymentModelPricingTablesFactory {
  constructor(paymentModel, planType) {
    this.paymentModel = paymentModel;
    this.planType = planType;

    this.PAYMENT_MODEL = PAYMENT_MODEL;
    this.METERED_SERVICES = METERED_SERVICES;
  }

  getTableClass() {
    const data = {
      [this.PAYMENT_MODEL.METERED]: MeteredPlanPricingNonUsageTable,
      [this.PAYMENT_MODEL.CAPACITY]: CapacityPlanPricingNonUsageTable,
    };
    if (
      this.paymentModel === this.PAYMENT_MODEL.CAPACITY &&
      Object.values(this.METERED_SERVICES).includes(this.planType)
    ) {
      return data[this.PAYMENT_MODEL.METERED];
    }
    return data[this.paymentModel];
  }
}

export const PlanPricingUsageTable = ({ data }) => {
  const query = planDetailsApi.useGetFieldDescriptionQuery();
  const fieldDescriptions = query.data || {};

  const [hiddenPriceMinColumns, setHiddenPriceMinColumns] = useState([]);
  useEffect(() => {
    if (data.length > 0) {
      const columns = Object.entries(data[0])
        .filter(([key, value]) => value === null && key.includes('price_min'))
        .map((key) => key[0]);

      setHiddenPriceMinColumns(columns);
    }
  }, [data]);

  const columns = useMemo(
    () => [
      {
        Header: () =>
          renderHeaderWithHover(
            'Call set-up',
            'call_set_up',
            fieldDescriptions
          ),
        accessor: 'call_set_up',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Price per min Fix',
            'price_min_fix',
            fieldDescriptions
          ),
        accessor: 'price_min_fix',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Price per min Mob',
            'price_min_mob',
            fieldDescriptions
          ),
        accessor: 'price_min_mob',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Price per min Pay Phone',
            'price_min_payphone',
            fieldDescriptions
          ),
        accessor: 'price_min_payphone',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Price per min unknown',
            'price_min_unknown',
            fieldDescriptions
          ),
        accessor: 'price_min_unknown',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Price per min Shadow',
            'price_min_shadow_routing',
            fieldDescriptions
          ),
        accessor: 'price_min_shadow_routing',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Price per min Fixed Rodasy',
            'price_min_fixed_rodasy',
            fieldDescriptions
          ),
        accessor: 'price_min_fixed_rodasy',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Price per min Mobile Rodasy',
            'price_min_mobile_rodasy',
            fieldDescriptions
          ),
        accessor: 'price_min_mobile_rodasy',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Price per min International',
            'price_min_international',
            fieldDescriptions
          ),
        accessor: 'price_min_international',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Billing Increment',
            'billing_increment',
            fieldDescriptions
          ),
        accessor: 'billing_increment',
        className: 'col-md-2',
      },
    ],
    [hiddenPriceMinColumns]
  );

  return data.length > 0 ? (
    <BootstrapTable
      columns={columns}
      hiddenColumns={hiddenPriceMinColumns}
      data={data}
      tableProps={() => ({
        responsive: true,
        bordered: true,
        className: 'normalFontSize',
      })}
      tableHeader="Usage"
    />
  ) : null;
};

export const PlanPricingContractTable = ({ data }) => {
  const query = planDetailsApi.useGetFieldDescriptionQuery();
  const fieldDescriptions = query.data || {};
  const columns = useMemo(
    () => [
      {
        id: 'duration',
        Header: () =>
          renderHeaderWithHover('Duration', 'duration', fieldDescriptions),
        accessor: 'duration',
        className: 'col-md-1',
      },
      {
        id: 'cancellation',
        Header: () =>
          renderHeaderWithHover(
            'Cancellation',
            'cancellation',
            fieldDescriptions
          ),
        accessor: 'cancellation',
        className: 'col-md-1',
      },
    ],
    []
  );
  return data.length > 0 ? (
    <BootstrapVerticalColumnTable
      columns={columns}
      data={data}
      tableProps={() => ({
        responsive: true,
        bordered: true,
        className: 'normalFontSize',
      })}
      tableHeader="Contract"
      hideHeader
    />
  ) : null;
};

export const PlanPricingOutboundTable = ({ data, plan }) => {
  const columns = useMemo(
    () => [
      {
        Header: 'Destination',
        accessor: 'destination',
        className: 'col-md-3',
      },
      {
        Header: 'Call setup',
        accessor: 'callSetup',
        className: 'col-md-1',
      },
      {
        Header: 'Price per minute',
        accessor: 'pricePerMinute',
        className: 'col-md-1',
      },
      {
        Header: 'Currency Traffic',
        accessor: 'currencyTraffic',
        className: 'col-md-1',
      },
      {
        Header: 'Billing Method',
        accessor: 'billingMethod',
        className: 'col-md-1',
      },
    ],
    []
  );
  return (
    <BootstrapTable
      columns={columns}
      data={data}
      tableProps={() => ({
        responsive: true,
        bordered: true,
        className: 'normalFontSize',
      })}
      tableHeader={`Outbound Plan ${plan || ''}`}
    />
  );
};
